import { CreateOrg } from "@garther/interfaces";
import { Box, Button, ImageInput, Input, TiptapEditor } from "@garther/ui/components";
import { toast, useI18n } from "@garther/ui/libs";
import { FormApi, ReactFormApi } from "@tanstack/react-form";

import { useUploadImage } from "@/features/public/hooks/use-public-query";

interface OrgFormProps {
  form: FormApi<CreateOrg, undefined> & ReactFormApi<CreateOrg, undefined>;
  isSubmitting: boolean;
  onCancel: () => void;
}

export function OrgForm({ form, isSubmitting, onCancel }: OrgFormProps) {
  const { t } = useI18n();
  const { mutateAsync: uploadImage } = useUploadImage();

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <Box className="space-y-6 mb-6" label={t("org.edit.basicInfo")}>
        {/* Basic Information Section */}
        <div className="space-y-4">
          {/* Cover */}
          <form.Field
            name="cover"
            validators={{
              onChange: ({ value }) => {
                if (value && value.trim() && !/^https?:\/\/.+/.test(value.trim())) {
                  return t("org.edit.errors.invalidUrl");
                }
                return undefined;
              }
            }}
          >
            {(field) => (
              <ImageInput
                value={field.state.value}
                onChange={field.handleChange}
                uploadFn={uploadImage}
                label={t("org.edit.cover")}
                required={true}
                previewSize="w-full h-48 md:h-64"
                previewClassName="w-full h-48 md:h-64 object-cover"
                disabled={isSubmitting}
                toastSuccess={toast.success}
                toastError={toast.error}
                successMessage={t("org.edit.coverUploadSuccess")}
                errorMessage={t("org.edit.coverUploadError")}
                invalidImageTypeMessage={t("org.edit.errors.invalidImageType")}
                fileTooLargeMessage={t("profile.basicInfo.fileTooLarge")}
                alt="Cover preview"
              />
            )}
          </form.Field>

          <div className="flex flex-col sm:flex-row gap-4 md:gap-8 w-full">
            {/* Avatar */}
            <form.Field
              name="avatar"
              validators={{
                onChange: ({ value }) => {
                  if (value && value.trim() && !/^https?:\/\/.+/.test(value.trim())) {
                    return t("org.edit.errors.invalidUrl");
                  }
                  return undefined;
                }
              }}
            >
              {(field) => (
                <ImageInput
                  value={field.state.value}
                  onChange={field.handleChange}
                  uploadFn={uploadImage}
                  label={t("org.edit.avatar")}
                  required={true}
                  previewSize="w-36 h-36"
                  disabled={isSubmitting}
                  toastSuccess={toast.success}
                  toastError={toast.error}
                  successMessage={t("org.edit.avatarUploadSuccess")}
                  errorMessage={t("org.edit.avatarUploadError")}
                  invalidImageTypeMessage={t("org.edit.errors.invalidImageType")}
                  fileTooLargeMessage={t("profile.basicInfo.fileTooLarge")}
                  alt="Avatar preview"
                />
              )}
            </form.Field>

            <div className="flex-1 space-y-4">
              {/* Name */}
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => (!value?.trim() ? t("org.edit.errors.nameRequired") : undefined)
                }}
              >
                {(field) => (
                  <Input
                    label={t("org.edit.name")}
                    value={field.state.value || ""}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t("org.edit.namePlaceholder")}
                    required
                    error={field.state.meta.errors[0] || undefined}
                    disabled={isSubmitting}
                  />
                )}
              </form.Field>

              {/* Tagline */}
              <form.Field name="tagline">
                {(field) => (
                  <Input
                    label={t("org.edit.tagline")}
                    value={field.state.value || ""}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t("org.edit.taglinePlaceholder")}
                    required
                    error={field.state.meta.errors[0] || undefined}
                    disabled={isSubmitting}
                  />
                )}
              </form.Field>
            </div>
          </div>

          {/* Description */}
          <form.Field
            name="description"
            validators={{
              onChange: ({ value }) => (!value?.trim() ? t("org.edit.errors.descriptionRequired") : undefined)
            }}
          >
            {(field) => (
              <TiptapEditor
                label={t("org.edit.description")}
                required
                error={field.state.meta.errors[0] || undefined}
                value={field.state.value || ""}
                onChange={field.handleChange}
                disabled={isSubmitting}
                placeholder={t("org.edit.descriptionPlaceholder")}
              />
            )}
          </form.Field>

          {/* Mission */}
          <form.Field name="mission">
            {(field) => (
              <TiptapEditor
                label={t("org.edit.mission")}
                error={field.state.meta.errors[0] || undefined}
                value={field.state.value || ""}
                onChange={field.handleChange}
                disabled={isSubmitting}
                placeholder={t("org.edit.missionPlaceholder")}
              />
            )}
          </form.Field>
        </div>
      </Box>

      <Box className="space-y-6 mb-6" label={t("org.edit.contactInfo")}>
        {/* Contact Information Section */}
        <div className="space-y-4">
          {/* Email */}
          <form.Field
            name="contact.email"
            validators={{
              onChange: ({ value }) => {
                if (value && value.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
                  return t("org.edit.errors.invalidEmail");
                }
                return undefined;
              }
            }}
          >
            {(field) => (
              <Input
                label={t("org.edit.email")}
                type="email"
                value={field.state.value || ""}
                onChange={(e) => field.handleChange(e.target.value || null)}
                placeholder="contact@example.com"
                error={field.state.meta.errors[0] || undefined}
                disabled={isSubmitting}
              />
            )}
          </form.Field>

          {/* Phone */}
          <form.Field name="contact.phone">
            {(field) => (
              <Input
                label={t("org.edit.phone")}
                type="tel"
                value={field.state.value || ""}
                onChange={(e) => field.handleChange(e.target.value || null)}
                placeholder="+84 123 456 789"
                error={field.state.meta.errors[0] || undefined}
                disabled={isSubmitting}
              />
            )}
          </form.Field>

          {/* Website */}
          <form.Field
            name="contact.website"
            validators={{
              onChange: ({ value }) => {
                if (value && value.trim() && !/^https?:\/\/.+/.test(value.trim())) {
                  return t("org.edit.errors.invalidUrl");
                }
                return undefined;
              }
            }}
          >
            {(field) => (
              <Input
                label={t("org.edit.website")}
                type="url"
                value={field.state.value || ""}
                onChange={(e) => field.handleChange(e.target.value || null)}
                placeholder="https://example.com"
                error={field.state.meta.errors[0] || undefined}
                disabled={isSubmitting}
              />
            )}
          </form.Field>

          {/* Social Media */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Instagram */}
            <form.Field
              name="contact.instagram"
              validators={{
                onChange: ({ value }) => {
                  if (value && value.trim() && !/^https?:\/\/.+/.test(value.trim())) {
                    return t("org.edit.errors.invalidUrl");
                  }
                  return undefined;
                }
              }}
            >
              {(field) => (
                <Input
                  label={t("org.edit.instagram")}
                  type="url"
                  value={field.state.value || ""}
                  onChange={(e) => field.handleChange(e.target.value || null)}
                  placeholder="https://instagram.com/..."
                  error={field.state.meta.errors[0] || undefined}
                  disabled={isSubmitting}
                />
              )}
            </form.Field>

            {/* Facebook */}
            <form.Field
              name="contact.facebook"
              validators={{
                onChange: ({ value }) => {
                  if (value && value.trim() && !/^https?:\/\/.+/.test(value.trim())) {
                    return t("org.edit.errors.invalidUrl");
                  }
                  return undefined;
                }
              }}
            >
              {(field) => (
                <Input
                  label={t("org.edit.facebook")}
                  type="url"
                  value={field.state.value || ""}
                  onChange={(e) => field.handleChange(e.target.value || null)}
                  placeholder="https://facebook.com/..."
                  error={field.state.meta.errors[0] || undefined}
                  disabled={isSubmitting}
                />
              )}
            </form.Field>

            {/* Twitter */}
            <form.Field
              name="contact.twitter"
              validators={{
                onChange: ({ value }) => {
                  if (value && value.trim() && !/^https?:\/\/.+/.test(value.trim())) {
                    return t("org.edit.errors.invalidUrl");
                  }
                  return undefined;
                }
              }}
            >
              {(field) => (
                <Input
                  label={t("org.edit.twitter")}
                  type="url"
                  value={field.state.value || ""}
                  onChange={(e) => field.handleChange(e.target.value || null)}
                  placeholder="https://twitter.com/..."
                  error={field.state.meta.errors[0] || undefined}
                  disabled={isSubmitting}
                />
              )}
            </form.Field>

            {/* YouTube */}
            <form.Field
              name="contact.youtube"
              validators={{
                onChange: ({ value }) => {
                  if (value && value.trim() && !/^https?:\/\/.+/.test(value.trim())) {
                    return t("org.edit.errors.invalidUrl");
                  }
                  return undefined;
                }
              }}
            >
              {(field) => (
                <Input
                  label={t("org.edit.youtube")}
                  type="url"
                  value={field.state.value || ""}
                  onChange={(e) => field.handleChange(e.target.value || null)}
                  placeholder="https://youtube.com/..."
                  error={field.state.meta.errors[0] || undefined}
                  disabled={isSubmitting}
                />
              )}
            </form.Field>
          </div>
        </div>
      </Box>

      {/* Submit Buttons */}
      <div className="flex gap-4 justify-end pt-4">
        <Button
          type="button"
          variant="outline"
          padding="px-12 py-3"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {t("common.cancel")}
        </Button>
        <form.Subscribe selector={(state) => state.isDirty}>
          {(isDirty) => (
            <Button type="submit" padding="px-12 py-3" variant="default" disabled={isSubmitting || !isDirty}>
              {isSubmitting ? t("common.saving") : t("common.save")}
            </Button>
          )}
        </form.Subscribe>
      </div>
    </form>
  );
}
